import primitives from '@veneer/primitives'
import { extendSemantics } from '@veneer/semantics'
import { ThemeProvider as VeneerThemeProvider } from '@veneer/theme'
import React from 'react'
import { createGlobalStyle } from 'styled-components'
import { useResolvedThemeMode, useSignupSession } from '../../../hooks'
// import { hpxTokens } from './hpxTokens'
import { SignupTheme, hpxDarkTheme, hpxLightTheme } from './theme'

export const DEFAULT_MODE = 'light'
export const MODE = DEFAULT_MODE
export const DENSITY = 'standard'

export const shape = (isHpx: boolean) => {
  return isHpx ? 'pill' : 'round'
}

import rgba from 'polished/lib/color/rgba'

const hpxPrimitives = {
  color: {
    manatee0: '#fdfdff',
    manatee1: '#f5f6ff',
    manatee2: '#edeefe',
    manatee3: '#e0e1ee',
    manatee4: '#d1d2e4',
    manatee5: '#b5b7cb',
    manatee6: '#9b9cb1',
    manatee7: '#828398',
    manatee8: '#696b7e',
    manatee9: '#525365',
    manatee10: '#3c3d4b',
    manatee11: '#292a35',
    manatee12: '#191a22'
  }
}

const hpxSemantics = extendSemantics({
  color: {
    light: {
      accent: {
        background: {
          active: hpxPrimitives.color.manatee2,
          default: hpxPrimitives.color.manatee0,
          hover: hpxPrimitives.color.manatee1
        },
        base: {
          active: rgba(hpxPrimitives.color.manatee7, 0.6),
          default: rgba(hpxPrimitives.color.manatee7, 0.2),
          hover: rgba(hpxPrimitives.color.manatee7, 0.4)
        },
        foreground: {
          active: primitives.color.gray12,
          default: primitives.color.gray12,
          hover: primitives.color.gray12
        }
      },
      background: {
        container: {
          active: hpxPrimitives.color.manatee2,
          hover: hpxPrimitives.color.manatee1
        },
        default: hpxPrimitives.color.manatee3,
        disabled: hpxPrimitives.color.manatee2,
        medium: hpxPrimitives.color.manatee1
      },
      caution: {
        base: {
          active: primitives.color.gold1,
          hover: primitives.color.gold2
        }
      },
      effects: {
        dropShadow: rgba(hpxPrimitives.color.manatee8, 0.2),
        glasswall: rgba(hpxPrimitives.color.manatee7, 0.6),
        highlight: {
          active: rgba(primitives.color.royalBlue3, 0.6),
          default: rgba(primitives.color.royalBlue5, 0.1),
          hover: rgba(primitives.color.royalBlue4, 0.3)
        }
      },
      focus: {
        default: primitives.color.royalBlue9
      },
      foreground: {
        default: hpxPrimitives.color.manatee12,
        disabled: hpxPrimitives.color.manatee5,
        light: hpxPrimitives.color.manatee8,
        medium: hpxPrimitives.color.manatee9
      },
      help: {
        background: {
          active: primitives.color.mediumOrchid2,
          default: primitives.color.mediumOrchid0,
          hover: primitives.color.mediumOrchid1
        },
        base: {
          active: primitives.color.mediumOrchid9,
          default: primitives.color.mediumOrchid7,
          hover: primitives.color.mediumOrchid8
        }
      },
      neutral: {
        background: {
          active: rgba(hpxPrimitives.color.manatee7, 0.2),
          default: hpxPrimitives.color.manatee1,
          hover: rgba(hpxPrimitives.color.manatee7, 0.1)
        },
        base: {
          active: hpxPrimitives.color.manatee9,
          default: hpxPrimitives.color.manatee7,
          hover: hpxPrimitives.color.manatee8
        }
      },
      primary: {
        background: {
          active: primitives.color.royalBlue2,
          default: primitives.color.royalBlue0,
          hover: primitives.color.royalBlue1
        },
        base: {
          active: primitives.color.royalBlue9,
          default: primitives.color.royalBlue7,
          hover: primitives.color.royalBlue8
        }
      },
      spotlight: {
        background: {
          default: hpxPrimitives.color.manatee11
        },
        foreground: {
          active: hpxPrimitives.color.manatee11,
          default: hpxPrimitives.color.manatee11,
          hover: hpxPrimitives.color.manatee11
        }
      },
      stroke: {
        decorative: {
          active: hpxPrimitives.color.manatee4,
          default: hpxPrimitives.color.manatee2,
          hover: hpxPrimitives.color.manatee3
        },
        informative: {
          active: hpxPrimitives.color.manatee9,
          default: hpxPrimitives.color.manatee7,
          hover: hpxPrimitives.color.manatee8
        }
      }
    },
    dark: {
      accent: {
        background: {
          active: hpxPrimitives.color.manatee9,
          default: hpxPrimitives.color.manatee12,
          hover: hpxPrimitives.color.manatee10
        },
        base: {
          active: rgba(hpxPrimitives.color.manatee9, 0.6),
          default: rgba(hpxPrimitives.color.manatee9, 0.2),
          hover: rgba(hpxPrimitives.color.manatee9, 0.4)
        },
        foreground: {
          active: primitives.color.white,
          default: primitives.color.white,
          hover: primitives.color.white
        }
      },
      background: {
        container: {
          active: hpxPrimitives.color.manatee9,
          default: hpxPrimitives.color.manatee11,
          hover: hpxPrimitives.color.manatee10
        },
        default: hpxPrimitives.color.manatee12,
        disabled: rgba(hpxPrimitives.color.manatee8, 0.1),
        medium: hpxPrimitives.color.manatee10
      },
      effects: {
        glasswall: rgba(hpxPrimitives.color.manatee7, 0.6),
        highlight: {
          active: rgba(primitives.color.royalBlue6, 0.6),
          default: rgba(primitives.color.royalBlue1, 0.1),
          hover: rgba(primitives.color.royalBlue3, 0.3)
        }
      },
      focus: {
        default: primitives.color.royalBlue9,
        inverse: hpxPrimitives.color.manatee11
      },
      foreground: {
        default: hpxPrimitives.color.manatee0,
        disabled: hpxPrimitives.color.manatee8,
        light: hpxPrimitives.color.manatee5,
        medium: hpxPrimitives.color.manatee4
      },
      help: {
        background: {
          default: primitives.color.mediumOrchid12,
          hover: primitives.color.mediumOrchid11,
          active: primitives.color.mediumOrchid10
        },
        base: {
          default: primitives.color.mediumOrchid5,
          hover: primitives.color.mediumOrchid4,
          active: primitives.color.mediumOrchid3
        }
      },
      neutral: {
        background: {
          active: rgba(hpxPrimitives.color.manatee6, 0.2),
          default: hpxPrimitives.color.manatee10,
          hover: rgba(hpxPrimitives.color.manatee6, 0.1)
        },
        base: {
          active: hpxPrimitives.color.manatee4,
          default: hpxPrimitives.color.manatee6,
          hover: hpxPrimitives.color.manatee5
        },
        foreground: {
          active: hpxPrimitives.color.manatee12,
          default: hpxPrimitives.color.manatee12,
          hover: hpxPrimitives.color.manatee12
        }
      },
      primary: {
        background: {
          active: rgba(hpxPrimitives.color.manatee2, 0.1),
          default: hpxPrimitives.color.manatee12,
          hover: rgba(hpxPrimitives.color.manatee1, 0.1)
        },
        base: {
          default: primitives.color.royalBlue5,
          hover: primitives.color.royalBlue4,
          active: primitives.color.royalBlue3
        },
        foreground: {
          active: hpxPrimitives.color.manatee12,
          default: hpxPrimitives.color.manatee12,
          hover: hpxPrimitives.color.manatee12
        }
      },
      spotlight: {
        base: {
          default: hpxPrimitives.color.manatee11
        }
      },
      stroke: {
        decorative: {
          active: hpxPrimitives.color.manatee8,
          default: hpxPrimitives.color.manatee10,
          hover: hpxPrimitives.color.manatee9
        },
        informative: {
          active: hpxPrimitives.color.manatee5,
          default: hpxPrimitives.color.manatee7,
          hover: hpxPrimitives.color.manatee6
        }
      }
    }
  }
})

export const customSemantics = (isHpx?: boolean) => {
  return isHpx ? hpxSemantics : undefined
}

export const SignupGlobalStyle = createGlobalStyle<{ theme?: SignupTheme }>`
  :root {
    --signup-bg-main: ${({ theme }) => theme.bgMain};
    --signup-checkout-disabled-bg: ${({ theme }) => theme.checkoutDisabledBg};
    --signup-secondary-text: ${({ theme }) => theme.secondaryText};
    --signup-secondary-border: ${({ theme }) => theme.secondaryBorder};
    --signup-royal-blue-3: ${({ theme }) => theme.royalBlue3};
    --signup-royal-blue-7: ${({ theme }) => theme.royalBlue7};
    --signup-divider-color: ${({ theme }) => theme.dividerColor};
    --signup-subtitle-color: ${({ theme }) => theme.subtitleColor};
    --signup-card-bg: ${({ theme }) => theme.cardBg};
    --signup-card-surface: ${({ theme }) => theme.cardSurface};
    --signup-primary-accent-fg: ${({ theme }) => theme.primaryAccentFg};
    --signup-overlay-bg: ${({ theme }) => theme.overlayBg};
    --signup-overlay-bg-strong: ${({ theme }) => theme.overlayBgStrong};
    --signup-error-icon-color: ${({ theme }) => theme.errorIconColor};
  }
`

export const LayoutTheme = ({ children }) => {
  const { isHpx } = useSignupSession()
  const mode = useResolvedThemeMode()

  const signupTheme = mode === 'dark' ? hpxDarkTheme : hpxLightTheme

  return (
    <div className="layouttheme">
      <VeneerThemeProvider
        customSemantics={customSemantics(isHpx)}
        mode={mode}
        shape={shape(isHpx)}
        density={DENSITY}
      >
        <SignupGlobalStyle theme={signupTheme} />
        {children}
      </VeneerThemeProvider>
    </div>
  )
}
